import { useAuthMe } from '@pulse/auth-ui';
import { useQuery } from '@tanstack/react-query';
import * as React from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { Skeleton } from '@/components/ui/skeleton';
import { getAdminEnvironment } from '@/env';
import { apiRequest } from '@/lib/api-client';

/**
 * Wraps an authenticated route. While the initial `/auth/me` is in flight we
 * render a skeleton; on `null` we send the user to `/login` with a `next` so
 * they land back on the requested page after signing in.
 *
 * Group gating is handled separately by `<RequireGroup>` from `@pulse/auth-ui`,
 * applied in `App.tsx` once around the admin shell.
 */
export function ProtectedRoute({ children }: { readonly children: React.ReactNode }): React.ReactElement {
  const { user, loading } = useAuthMe();
  const location = useLocation();
  const devSession = useDevBypassSession();

  if (devSession.data === true) return <>{children}</>;
  // While the dev-bypass check is outstanding, wait for it rather than sending anyone to sign in.
  if ((loading && user === null) || (devInsecureSessionEnabled() && devSession.isPending)) {
    return (
      <div className="flex min-h-screen flex-col gap-4 p-8">
        <Skeleton className="h-10 w-1/3" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (user === null) {
    const search = new URLSearchParams({ next: `${location.pathname}${location.search}` }).toString();
    return <Navigate to={`/login?${search}`} replace />;
  }
  return <>{children}</>;
}

/**
 * Local development without Cognito: the API attaches a synthetic session to loopback requests
 * when API_DEV_INSECURE_SESSION is on. In Vite's dev server only, ask it whether there is one.
 */
function devInsecureSessionEnabled(): boolean {
  return getAdminEnvironment().devInsecureSession;
}

function useDevBypassSession() {
  return useQuery({
    queryKey: ['auth', 'dev-bypass-session'],
    enabled: devInsecureSessionEnabled(),
    retry: false,
    staleTime: Infinity,
    queryFn: async () => {
      try {
        const response = await apiRequest<{ authenticatedSession: unknown }>('/api/auth/session');
        return response.authenticatedSession !== null && response.authenticatedSession !== undefined;
      } catch {
        return false;
      }
    },
  });
}
